import {
  Button,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import dayjs from "dayjs";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Loader from "../../../../components/element/Loader";
import NoDataPlaceholder from "../../../../components/element/NoDataPlaceholder";
import TableButton from "../../../../components/element/TableButton";
import {
  AIRPORT_FEES_TYPE,
  COMMERCIAL_INVOICE_STATUS,
  DATE_FORMAT,
  FeeType,
  INVOICE_STATUS,
} from "../../../../utils/constants";
import ROUTE_PATHS from "../../../../utils/routePaths";
import { formatToTwoDecimal } from "../../../../utils/utils";
import "../InvoiceScreen.css";
import { ArrowRightOutlined, InfoCircleOutlined } from "@ant-design/icons";

function NewInvoiceTable({
  invoices,
  selectedTotal,
  selectedAirlineId,
  isLoading,
  onPayClick,
  selectedInvoices,
  setSelectedInvoices,
  currentPage,
}) {
  const navigate = useNavigate();
  const [tooltipOpen, setTooltipOpen] = useState({});

  // Create selectedSet directly
  const selectedSet = new Set(selectedInvoices);

  const handleCheckboxChange = (rowId) => {
    setSelectedInvoices((prev) => {
      if (prev.includes(rowId)) {
        return prev.filter((id) => id !== rowId);
      } else {
        return [...prev, rowId];
      }
    });
  };

  const handleSelectAllChange = () => {
    const allInvoiceIds = invoices.map((invoice) => invoice.id);
    const allSelected = allInvoiceIds.every((id) => selectedSet.has(id));

    if (allSelected) {
      // If all are selected, deselect all
      setSelectedInvoices([]);
    } else {
      // If not all are selected, select all
      setSelectedInvoices(allInvoiceIds);
    }
  };

  const isAllSelected = invoices.length > 0 && invoices.every((invoice) => selectedSet.has(invoice.id));

  const handleViewInvoice = (row) => {
    const enrichedInvoiceData = {
      ...row,
      invoiceStatus: INVOICE_STATUS.NEW_INVOICE,
      id: row.id,
    };
    if (row.feeType === FeeType.LAND_LEASE || row.feeType === FeeType.OFFICE_SPACE || row.feeType === FeeType.RESTAURANT || row.feeType === FeeType.COUNTER_SPACE) {
      navigate(ROUTE_PATHS.COMMERCIAL_AIRLINE_INVOICE_CONCESSION_FEE_DETAILS, {
        state: {
          invoiceData: enrichedInvoiceData,
          selectedAirlineId: selectedAirlineId,
          id: row.id,
          currentPage: currentPage,
        },
      });
    } else {
      navigate(ROUTE_PATHS.COMMERCIAL_AIRLINE_INVOICE_DETAILS, {
        state: {
          invoiceData: enrichedInvoiceData,
          selectedAirlineId: selectedAirlineId,
          id: row.id,
          currentPage: currentPage,
        },
      });
    }
  };

  const getFeeTypeName = (value) => {
    const feeType = AIRPORT_FEES_TYPE.find((fee) => fee.value === value);
    return feeType ? feeType.name : value;
  };

  if (isLoading) return <Loader />;

  if (invoices && invoices?.length === 0) {
    return <NoDataPlaceholder />;
  }

  return (
    <TableContainer component={Paper} className="invoices-data-table">
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>
              <Checkbox
                size="small"
                checked={isAllSelected}
                onChange={handleSelectAllChange}
              />
              No
            </TableCell>
            <TableCell>Invoice No</TableCell>
            <TableCell>Invoice Date</TableCell>
            <TableCell>Invoice Due Date</TableCell>
            <TableCell>Invoice Amount</TableCell>
            <TableCell>Currency</TableCell>
            <TableCell>View Invoice</TableCell>
            <TableCell>Type of Fees</TableCell>
            <TableCell>Customer Number</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {invoices.map((row, index) => (
            <TableRow key={index} className="table-row">
              <TableCell>
                <Checkbox
                  size="small"
                  checked={selectedInvoices.includes(row.id)}
                  onChange={() => handleCheckboxChange(row.id)}
                />
                {index + 1}
              </TableCell>
              <TableCell>{row.dynamicsInvoiceNumber}</TableCell>
              <TableCell>
                {dayjs(row.invoiceDate).format(DATE_FORMAT)}
              </TableCell>
              <TableCell>
                {row.dueDate ? dayjs(row.dueDate).format(DATE_FORMAT) : "---"}
              </TableCell>
              <TableCell>
                $
                {formatToTwoDecimal(
                  row.invoiceAmount - row.totalPaymentReceived
                )}
                {row.commercialInvoiceStatus === COMMERCIAL_INVOICE_STATUS.PARTIAL_PAYMENT && (
                  <Tooltip
                    open={tooltipOpen[row.id] || false}
                    onOpen={() => setTooltipOpen({ ...tooltipOpen, [row.id]: true })}
                    onClose={() => setTooltipOpen({ ...tooltipOpen, [row.id]: false })}
                    title={
                      <div
                        style={{
                          fontSize: "11px",
                          fontFamily: "Inter, sans-serif",
                        }}
                      >
                        <div>
                          Invoice Amount: BSD{" "}
                          {formatToTwoDecimal(row.invoiceAmount)}
                        </div>
                        <div>
                          Paid Amount: BSD{" "}
                          {formatToTwoDecimal(row.totalPaymentReceived)}
                        </div>
                      </div>
                    }
                    arrow
                    placement="bottom"
                    classes={{ tooltip: "partial-payment-tooltip" }}
                  >
                    <IconButton
                      size="small"
                      className="partial-payment-info-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        setTooltipOpen({ ...tooltipOpen, [row.id]: !tooltipOpen[row.id] });
                      }}
                    >
                      <InfoCircleOutlined />
                    </IconButton>
                  </Tooltip>
                )}
              </TableCell>
              <TableCell>{row.currency}</TableCell>
              <TableCell>
                <TableButton onClick={() => handleViewInvoice(row)}>
                  View
                </TableButton>
              </TableCell>
              <TableCell>{getFeeTypeName(row.feeType)}</TableCell>
              <TableCell>{row.dynamicsCustomerNumber}</TableCell>
            </TableRow>
          ))}
          {!isLoading && invoices.length > 0 && (
            <TableRow className="grand-total-row">
              <TableCell colSpan={6}>
                <div className="total-helper-container">
                  <Typography variant="h6">
                    Selected Total: ${formatToTwoDecimal(selectedTotal)}
                  </Typography>
                  <div className="payment-helper-text">
                    When you click the "Pay" button, you will be redirected to
                    the next screen to complete your payment.
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <Button
                  variant="contained"
                  color="approve"
                  onClick={() => {
                    onPayClick();
                  }}
                  endIcon={<ArrowRightOutlined style={{ fontSize: "15px" }} />}
                >
                  Pay
                </Button>
              </TableCell>
              <TableCell colSpan={6}></TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default NewInvoiceTable;
